import React from "react";
import { axiosPost } from "../../data/helpers/baseApi";
import { SERVICE_URLS } from "../../data/helpers/serviceUrls";
import { QUERY_KEYS } from "../../data/helpers/queryKeys";
import { useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import LoadingIndicator from "../../components/LoadingIndicator";
import OktaSignInWidget from "./OktaSignInWidget";
import { isOktaClientConfigured, oktaAuth } from "../../utils/oktaAuth";
import {
  clearBrokerLoggedOut,
  clearOktaAuthState,
} from "../../utils/oktaLogout";
import { clearSensitiveLoginUrl } from "../../utils/loginUrlCleanup";

const mapOAuthErrorToUserMessage = (error) => {
  const errorString = error?.message || String(error);

  if (errorString.includes("User is not assigned to the client application")) {
    return {
      code: "USER_NOT_ASSIGNED",
      message: "The user is not authorized to access this application.",
    };
  }

  if (
    errorString.includes("Invalid token") ||
    errorString.includes("invalid_grant")
  ) {
    return {
      code: "INVALID_TOKEN",
      message: "The session is invalid or has expired.",
    };
  }

  if (
    errorString.includes("Network") ||
    errorString.includes("Failed to fetch")
  ) {
    return {
      code: "NETWORK_ERROR",
      message: "Unable to connect to the authentication server.",
    };
  }

  if (errorString.includes("ERR_TIMED_OUT")) {
    return { code: "NETWORK_ERROR", message: "The connection timed out." };
  }

  return {
    code: "UNKNOWN_ERROR",
    message: "An error occurred while processing sign-in.",
  };
};

const isJwtIdToken = (token) => {
  if (typeof token !== "string") {
    return false;
  }

  const segments = token.split(".");
  return segments.length === 3 && segments.every((segment) => segment);
};

const getInvalidTokenErrorState = () => ({
  code: "INVALID_TOKEN",
  message: "The session is invalid or has expired.",
});

const BrokerLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const loginSearchParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const isLoginRoute = location.pathname === "/login";
  const isCallbackRoute = location.pathname === "/login/callback";
  const locationSearch = location.search;
  const testToken =
    process.env.NODE_ENV !== "production"
      ? loginSearchParams.get("testToken")
      : null;
  const shouldShowBrokerLoginPage = isLoginRoute && !testToken;

  const [, setIsAuthenticating] = React.useState(false);

  const authenticateWithBackend = React.useCallback(
    async (idToken) => {
      try {
        let authResponse = await axiosPost(SERVICE_URLS.BROKER_AUTHENTICATE, {
          idToken,
        });

        queryClient.invalidateQueries([QUERY_KEYS.USER_GET]);

        if (authResponse.data && authResponse.data.isAuthenticated) {
          navigate("/dashboard", { replace: true });
        } else {
          navigate("/login/error", {
            replace: true,
            state: {
              code: "BACKEND_ERROR",
              message: "Unable to validate your credentials.",
            },
          });
        }
      } catch (err) {
        const isNetworkError =
          !err?.response &&
          (err?.code === "ERR_NETWORK" || err?.message === "Network Error");
        navigate("/login/error", {
          replace: true,
          state: isNetworkError
            ? {
                code: "NETWORK_ERROR",
                message:
                  "Unable to reach the server. Please check that the application server is running and try again.",
              }
            : {
                code: "BACKEND_ERROR",
                message: "An error occurred while processing your request.",
              },
        });
      }
    },
    [navigate, queryClient],
  );

  React.useEffect(() => {
    if (!isLoginRoute && !isCallbackRoute) {
      setIsAuthenticating(false);
      return undefined;
    }

    if (isCallbackRoute) {
      clearBrokerLoggedOut();
    }

    if (shouldShowBrokerLoginPage) {
      if (!isOktaClientConfigured()) {
        navigate("/login/error", {
          replace: true,
          state: {
            code: "CONFIG_ERROR",
            message: "Authentication configuration is unavailable.",
          },
        });
        return undefined;
      }

      clearBrokerLoggedOut();
      clearOktaAuthState();
      clearSensitiveLoginUrl();
      setIsAuthenticating(false);
      return undefined;
    }

    let isMounted = true;

    const processLoginCallback = async () => {
      let shouldResetLoadingState = false;

      try {
        setIsAuthenticating(true);

        if (!isOktaClientConfigured()) {
          shouldResetLoadingState = true;
          navigate("/login/error", {
            replace: true,
            state: {
              code: "CONFIG_ERROR",
              message: "Authentication configuration is unavailable.",
            },
          });
          return;
        }

        const callbackParams = new URLSearchParams(locationSearch);
        const authCode = callbackParams.get("code");
        const authError = callbackParams.get("error");

        if (authError) {
          shouldResetLoadingState = true;
          const errorInfo = mapOAuthErrorToUserMessage(new Error(authError));
          navigate("/login/error", { replace: true, state: errorInfo });
          return;
        }

        if (!authCode) {
          shouldResetLoadingState = true;
          navigate("/login", { replace: true });
          return;
        }

        shouldResetLoadingState = true;
        try {
          const { tokens } = await oktaAuth.token.parseFromUrl();
          const idToken = tokens?.idToken?.idToken;
          clearSensitiveLoginUrl();

          if (!isJwtIdToken(idToken)) {
            navigate("/login/error", {
              replace: true,
              state: getInvalidTokenErrorState(),
            });
            return;
          }

          await authenticateWithBackend(idToken);
        } catch (parseErr) {
          clearSensitiveLoginUrl();
          const errorInfo = mapOAuthErrorToUserMessage(parseErr);
          navigate("/login/error", {
            replace: true,
            state: errorInfo,
          });
        }
      } catch (err) {
        shouldResetLoadingState = true;
        queryClient.invalidateQueries([QUERY_KEYS.USER_GET]);

        if (isMounted) {
          const errorInfo = mapOAuthErrorToUserMessage(err);
          navigate("/login/error", {
            replace: true,
            state: errorInfo,
          });
        }
      } finally {
        if (isMounted && shouldResetLoadingState) {
          setIsAuthenticating(false);
        }
      }
    };

    if (isLoginRoute && testToken) {
      const doTestTokenAuth = async () => {
        try {
          setIsAuthenticating(true);
          await authenticateWithBackend(testToken);
        } finally {
          if (isMounted) {
            setIsAuthenticating(false);
          }
        }
      };
      doTestTokenAuth();
    } else {
      processLoginCallback();
    }

    return () => {
      isMounted = false;
    };
  }, [
    authenticateWithBackend,
    isCallbackRoute,
    isLoginRoute,
    locationSearch,
    navigate,
    queryClient,
    shouldShowBrokerLoginPage,
    testToken,
  ]);

  if (shouldShowBrokerLoginPage) {
    return (
      <OktaSignInWidget
        onSuccess={async (tokens) => {
          const idToken = tokens?.idToken?.idToken;

          if (!isJwtIdToken(idToken)) {
            navigate("/login/error", {
              replace: true,
              state: getInvalidTokenErrorState(),
            });
            return;
          }

          setIsAuthenticating(true);
          clearBrokerLoggedOut();
          await authenticateWithBackend(idToken);
          setIsAuthenticating(false);
        }}
        onError={(error) => {
          const errorInfo = mapOAuthErrorToUserMessage(error);
          navigate("/login/error", {
            replace: true,
            state: errorInfo,
          });
        }}
      />
    );
  }

  return <LoadingIndicator />;
};

export default BrokerLogin;
